<template>
  <div class="calendar-page-wrapper" style="position: relative">
    <div :class="{ 'blurred-container': shouldBlock }" class="calendar-inner">
      <!-- ═══ Barra de controles ═══ -->
      <div class="calendar-toolbar">
        <!-- Esquerda: navegação -->
        <div class="toolbar-left">
          <button class="pill-btn pill-btn--today" @click="resetToToday">
            Hoje
          </button>

          <div class="nav-arrows">
            <button class="arrow-btn" @click="$refs.calendar.prev()">
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <button class="arrow-btn" @click="$refs.calendar.next()">
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>

          <h2 class="toolbar-date">{{ formattedDate }}</h2>
        </div>

        <!-- Centro: clinic select -->
        <div v-if="mode === 'clinic'" class="toolbar-center" data-tour="cal-clinic-select">
          <v-select
            v-model="selectedClinics"
            :items="clinics"
            item-text="name"
            item-value="id"
            :return-object="true"
            multiple
            @update:modelValue="onClinicChange"
            label="Selecionar Clínicas"
            hide-details
            class="clinic-select"
            prepend-inner-icon="mdi-hospital-building"
            outlined
            dense
            color="blue"
            :item-disabled="(item) => !item.has_google_account"
          >
            <template v-slot:selection="{ item, index }">
              <template v-if="index < 2">
                <v-chip
                  close
                  small
                  @click:close="selectedClinics.splice(index, 1)"
                  class="clinic-chip-modern"
                >
                  <span class="chip-text">{{ item.name }}</span>
                </v-chip>
              </template>
              <template v-else-if="index === 2">
                <span class="chip-counter-text"
                  >+{{ selectedClinics.length - 2 }} outras</span
                >
              </template>
            </template>

            <template v-slot:item="{ item }">
              <div class="py-2">
                <div class="font-weight-medium">{{ item.name }}</div>
                <div
                  v-if="item.has_google_account"
                  class="text--secondary text-caption"
                >
                  CNPJ: {{ item.cnpj }} - Estado: {{ item.state }} - Cidade:
                  {{ item.city }}
                </div>
                <div v-else class="text-caption red--text font-weight-bold">
                  ⚠ Conta Google não vinculada
                </div>
              </div>
            </template>
          </v-select>
        </div>

        <!-- Direita: badge de modo -->
        <div class="toolbar-right">
          <span
            class="mode-badge"
            :class="
              mode === 'clinic' ? 'mode-badge--clinic' : 'mode-badge--doctor'
            "
          >
            <v-icon size="14" class="mr-1">
              {{
                mode === "clinic" ? "mdi-hospital-building" : "mdi-stethoscope"
              }}
            </v-icon>
            {{ mode === "clinic" ? "Agenda Clínica" : "Agenda Médico" }}
          </span>
        </div>
      </div>

      <!-- Barra de filtro de médicos (apenas no modo clinic) -->
      <DoctorsFilterBar
        v-if="mode === 'clinic' && availableDoctors.length > 0"
        data-tour="cal-doctors"
        :doctors="availableDoctors"
        :value="selectedDoctorIds"
        @input="onDoctorFilterChange"
      />

      <!-- Loading bar -->
      <v-progress-linear
        v-if="loadingCalendarData || loadingDoctors"
        indeterminate
        color="blue"
        height="2"
        class="calendar-loading-bar"
      />

      <!-- ═══ Calendário — ocupa exatamente o restante da tela ═══ -->
      <v-calendar
        ref="calendar"
        v-model="localDate"
        :weekdays="weekdays"
        :type="localType"
        :events="events"
        :first-interval="7"
        :interval-count="12"
        :interval-minutes="60"
        color="primary"
        locale="pt-BR"
        class="main-calendar"
        @input="onDateSelected"
        @click:date="viewDay"
        @click:time="onTimeClicked"
        @click:event="showEvent"
        @click:more="abrirMaisEventos"
        @change="updateRange"
      >
        <template v-slot:event="{ event, timed }">
          <div
            v-if="event.synthetic === 'locked'"
            class="locked-band"
            :title="event.name"
          >
            <v-icon size="11" color="white" class="locked-band__icon">mdi-lock</v-icon>
            <span class="locked-band__label">{{ event.name }}</span>
          </div>
          <div
            v-else
            class="clinic-event-content"
            :style="
              (mode === 'clinic' || event.confirmation_state === 'declined') &&
              event.status_color
                ? { borderLeftColor: event.status_color }
                : {}
            "
            :class="{
              'clinic-event-content--status':
                (mode === 'clinic' ||
                  event.confirmation_state === 'declined') &&
                !!event.status_color,
              'clinic-event-content--external': event.source === 'external',
              // salvia-789: a paciente avisou que nao vem. Fica com a cara de
              // cancelada (decisao do Ariel em 09/09) para o medico ver de
              // relance que o horario esta morto — mas o horario segue
              // reservado: `status` nao mudou, so a apresentacao.
              'clinic-event-content--declined':
                event.confirmation_state === 'declined',
            }"
          >
            <div class="clinic-event-content__main">
              <v-icon
                v-if="event.source === 'external'"
                size="11"
                color="white"
                class="external-icon"
              >
                mdi-google
              </v-icon>
              <span v-if="timed" class="clinic-event-content__time">
                {{ extractTime(event.start) }}
              </span>
              <span class="clinic-event-content__name">{{ event.name }}</span>
            </div>
            <!-- salvia-789 (D2): os tres estados de confirmacao passam a ter nome.
                 "Nao confirmada" descrevia dois deles ao mesmo tempo. Sem lembrete
                 enviado nao ha rotulo — poluiria a agenda inteira. -->
            <span
              v-if="event.confirmation_label"
              class="clinic-event-content__estado"
              :class="`clinic-event-content__estado--${event.confirmation_state}`"
              :title="event.confirmation_label"
            >
              {{ event.confirmation_label }}
            </span>
            <span
              v-if="event.clinic_name"
              class="clinic-event-content__clinic"
              :title="event.clinic_name"
            >
              <v-icon size="9" color="white" class="clinic-event-content__clinic-icon">
                mdi-hospital-building
              </v-icon>
              {{ event.clinic_name }}
            </span>
          </div>
        </template>

        <!-- Fase 2: overlay de disponibilidade (só com 1 médico em foco).
             Fundo bloqueado + janelas disponíveis em verde, atrás dos eventos. -->
        <template v-slot:day-body="{ date, timeToY }">
          <div v-if="showBlockedOverlay(date)" class="avail-overlay">
            <div class="avail-overlay__blocked"></div>
            <div
              v-for="(w, i) in availabilityByDate[date] || []"
              :key="i"
              class="avail-overlay__free"
              :style="{
                top: timeToY(w.from) + 'px',
                height: Math.max(0, timeToY(w.to) - timeToY(w.from)) + 'px',
              }"
            ></div>
          </div>
        </template>
      </v-calendar>

      <!--
        "+N" do mês: mini diálogo com TODOS os eventos do dia, no estilo do
        Google Agenda. Abre colado ao "+N" clicado; clicar num evento abre o
        detalhe dele (o mesmo diálogo de sempre).
      -->
      <v-menu
        v-model="maisEventos.aberto"
        :position-x="maisEventos.x"
        :position-y="maisEventos.y"
        absolute
        offset-y
        :close-on-content-click="false"
        max-width="300"
        min-width="260"
        content-class="mais-eventos-menu"
        transition="scale-transition"
      >
        <v-card class="mais-eventos">
          <header class="mais-eventos__topo">
            <div class="mais-eventos__data">
              <span class="mais-eventos__semana">{{ maisEventosSemana }}</span>
              <span class="mais-eventos__dia">{{ maisEventosDia }}</span>
            </div>
            <v-btn class="close-btn" icon aria-label="Fechar" @click="maisEventos.aberto = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </header>

          <div class="mais-eventos__lista">
            <button
              v-for="(evento, i) in eventosDoDiaMais"
              :key="`${evento.id || evento.google_id || 'ev'}-${i}`"
              type="button"
              class="mais-eventos__item"
              :class="{
                'mais-eventos__item--recusado': evento.confirmation_state === 'declined',
              }"
              :style="estiloDoItemMais(evento)"
              :title="evento.name"
              @click="abrirEventoDaLista(evento, $event)"
            >
              <v-icon
                v-if="evento.source === 'external'"
                x-small
                color="white"
                class="mr-1"
              >mdi-google</v-icon>
              <span class="mais-eventos__hora">{{ horarioNoDiaMais(evento) }}</span>
              <span class="mais-eventos__nome">{{ evento.name }}</span>
            </button>

            <p v-if="!eventosDoDiaMais.length" class="mais-eventos__vazio">
              Nenhum evento neste dia.
            </p>
          </div>
        </v-card>
      </v-menu>

      <!-- Modal de Detalhes do Evento -->
      <CalendarEventDetailsDialog
        v-model="selectedOpen"
        :event="selectedEvent"
        :details="selectedEventDetails"
        :loading="loadingEventDetails"
        @edit="editEvent"
        @delete="confirmDeleteEvent"
      />

      <!-- Dialog de Edição -->
      <CalendarDialogEditEvent
        v-model="OpenCalendarDialogEditEvent"
        :eventToEdit="eventToEditPro"
        @eventSaved="fetchEvents"
        @eventEdited="fetchEvents"
      />

    </div>

    <!-- Overlay de bloqueio -->
    <div
      v-if="shouldBlock"
      class="block-overlay d-flex flex-column align-center justify-center"
    >
      <v-icon color="red" size="48">mdi-alert-circle-outline</v-icon>

      <p class="mt-3 mb-4 text-subtitle-1 text-center font-weight-bold">
        <template v-if="mode === 'clinic'">
          <span v-if="clinicNoClinics">
            Nenhuma clínica encontrada. Cadastre ou solicite a criação de uma
            clínica para acessar a Agenda Clínica.
          </span>
          <span v-else-if="clinicNoneLinked">
            Nenhuma das suas clínicas tem uma Conta Google vinculada. Vincule
            uma conta em “Minhas Clínicas” para usar a Agenda Clínica.
          </span>
          <span v-else-if="clinicSelectedNotLinked">
            Esta clínica não possui conta Google vinculada.
          </span>
        </template>
        <template v-else-if="mode === 'doctor'">
          Sua conta não está vinculada ao Google
        </template>
      </p>

      <div data-tour="cal-vincular">
        <v-btn
          color="primary"
          v-if="mode === 'clinic' && clinicNoClinics"
          @click="$router.push('/my-clinics-page')"
        >
          Criar Clínica
        </v-btn>
        <v-btn
          color="primary"
          v-if="mode === 'clinic' && (clinicSelectedNotLinked || clinicNoneLinked)"
          @click="$router.push('/my-clinics-page')"
        >
          Vincular Conta Google
        </v-btn>
        <v-btn
          color="primary"
          v-if="mode === 'doctor'"
          @click="authenticateWithGoogle"
        >
          Vincular Conta Google
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { getLocalDateISO } from "@/utils/currentDayDate";
import CalendarDialogEditEvent from "@/features/calendar/components/CalendarDialogEditEvent.vue";
import CalendarEventDetailsDialog from "@/features/calendar/components/CalendarEventDetailsDialog.vue";
import DoctorsFilterBar from "@/features/calendar/components/DoctorsFilterBar.vue";
import { getMyClinics, getDoctorsByClinics } from "@/services/clinicService";
import { getResolvedSchedule } from "@/services/doctorScheduleService";
import { formatTime, todayISO, userTimeZone } from "@/utils/datetime";

export default {
  name: "CalendarPageContent",

  components: {
    CalendarDialogEditEvent,
    CalendarEventDetailsDialog,
    DoctorsFilterBar,
  },

  props: {
    type: {
      type: String,
      default: "month",
    },
    currentDate: {
      type: String,
    },
    mode: {
      type: String,
      default: "doctor",
    },
  },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      localDate: this.currentDate,
      localType: this.type,
      weekdays: [0, 1, 2, 3, 4, 5, 6],
      events: [],
      // "+N" do mês: qual dia está aberto e onde o mini diálogo aparece.
      maisEventos: { aberto: false, data: null, x: 0, y: 0 },
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      eventToEditPro: null,
      eventToDeleteId: null,
      clinics: [],
      selectedClinics: [],
      availableDoctors: [],
      selectedDoctorIds: [],
      // Fase 2: overlay de disponibilidade (só quando há 1 médico em foco)
      availabilityByDate: {}, // { 'YYYY-MM-DD': [{ from, to }] }
      overlayReady: false,
      loadingDoctors: false,
      loading: false,
      doctorHasGoogleAccount: null, // 👈 novo estado para doctor
      authChecked: false, // 👈 controlar quando já verificou
      clinicsLoaded: false,
      loadingCalendarData: false,
      OpenCalendarDialogEditEvent: false,
      selectedEventDetails: null,
      // salvia-789: id da consulta que veio da notificação, aguardando os eventos
      // carregarem para ser aberta. Limpo depois de abrir, para não reabrir sozinho
      // quando o médico navegar pelo calendário.
      eventoAlvoDaNotificacao: null,
      loadingEventDetails: false,
    };
  },

  mounted() {
    // salvia-789: chegou da notificação apontando para uma consulta específica.
    // Guarda o id e leva a agenda para a semana daquela data — sem isso o evento
    // pode nem estar carregado, porque a agenda abre sempre na semana corrente.
    const alvo = this.$route?.query?.event_id;
    if (alvo) {
      this.eventoAlvoDaNotificacao = String(alvo);
      const quando = this.$route?.query?.event_start;
      if (quando) {
        const d = new Date(quando);
        if (!Number.isNaN(d.getTime())) {
          this.localDate = d.toISOString().slice(0, 10);
        }
      }
    }

    if (this.mode === "clinic") {
      this.loadClinicsIfClinicMode().then(() => {
        if (
          this.selectedClinics &&
          this.selectedClinics.length > 0 &&
          !this.selectedClinics.some((c) => !c.has_google_account)
        ) {
          this.fetchEvents();
        } else {
          this.events = [];
        }
      });
    } else if (this.mode === "doctor") {
      this.checkGoogleAuth().then(() => {
        if (this.doctorHasGoogleAccount) {
          this.fetchEvents();
        } else {
          this.events = [];
        }
      });
    }
  },

  computed: {
    /**
     * Eventos do dia do "+N", em ordem de horário — os mesmos que o mês
     * mostraria se coubessem. Evento de vários dias aparece em todos os dias
     * que cobre. As faixas sintéticas (trava/bloqueio) ficam de fora: não são
     * eventos e não abrem detalhe.
     */
    eventosDoDiaMais() {
      const dia = this.maisEventos.data;
      if (!dia) return [];

      return (this.events || [])
        .filter((e) => {
          if (!e || e.synthetic) return false;
          const inicio = String(e.start || "").slice(0, 10);
          const fim = String(e.end || e.start || "").slice(0, 10);
          return inicio <= dia && dia <= fim;
        })
        .sort((a, b) => String(a.start).localeCompare(String(b.start)));
    },

    /** "SEG", "TER"... — como o Google Agenda mostra no topo do mini diálogo. */
    maisEventosSemana() {
      if (!this.maisEventos.data) return "";
      return new Date(`${this.maisEventos.data}T12:00:00`)
        .toLocaleDateString("pt-BR", { weekday: "short" })
        .replace(".", "")
        .toUpperCase();
    },

    maisEventosDia() {
      return this.maisEventos.data ? Number(this.maisEventos.data.slice(8, 10)) : "";
    },

    // ... seu formattedDate etc

    clinicNoClinics() {
      return (
        this.mode === "clinic" &&
        this.clinicsLoaded &&
        (!this.clinics || this.clinics.length === 0)
      );
    },

    clinicSelectedNotLinked() {
      return (
        this.mode === "clinic" &&
        this.selectedClinics &&
        this.selectedClinics.some((c) => !c.has_google_account)
      );
    },

    // Existem clínicas, mas NENHUMA tem conta Google — o select fica todo
    // desabilitado, então o usuário não consegue selecionar nem vincular por ali.
    // Sem isto, shouldBlock ficava false e o usuário travava sem orientação.
    clinicNoneLinked() {
      return (
        this.mode === "clinic" &&
        this.clinicsLoaded &&
        Array.isArray(this.clinics) &&
        this.clinics.length > 0 &&
        !this.clinics.some((c) => c.has_google_account)
      );
    },

    shouldBlock() {
      // Bloqueio para clínica: sem clínica, nenhuma vinculada, ou a selecionada não vinculada
      if (this.mode === "clinic") {
        if (this.clinicNoClinics) return true;
        if (this.clinicNoneLinked) return true;
        if (this.clinicSelectedNotLinked) return true;
        return false;
      }

      // Bloqueio para doctor
      if (
        this.mode === "doctor" &&
        this.authChecked &&
        this.doctorHasGoogleAccount === false
      ) {
        return true;
      }

      return false;
    },

    formattedDate() {
      const date = this.parseDate(this.localDate);

      const formatOptions = {
        day: {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        },
        month: {
          year: "numeric",
          month: "long",
        },
      };

      if (this.localType === "day") {
        return date.toLocaleDateString("pt-BR", formatOptions.day);
      }

      if (this.localType === "week") {
        const startOfWeek = this.getStartOfWeek(date);
        const endOfWeek = this.getEndOfWeek(startOfWeek);
        return `Semana de ${startOfWeek.toLocaleDateString(
          "pt-BR",
        )} a ${endOfWeek.toLocaleDateString("pt-BR")}`;
      }

      if (this.localType === "month") {
        return date.toLocaleDateString("pt-BR", formatOptions.month);
      }

      return "";
    },
  },

  watch: {
    // salvia-789: os eventos chegam por requisição, então não dá para abrir no
    // mounted. Assim que a lista existir, procura a consulta da notificação e abre.
    //
    // Este observador ficava num PRIMEIRO bloco `watch`, e este aqui era um
    // segundo: num objeto JavaScript a segunda chave apaga a primeira, então
    // ele nunca rodava — o clique na notificação levava à agenda, mas a
    // consulta não abria.
    events: {
      handler() {
        this.abrirEventoDaNotificacaoSePossivel();
      },
      immediate: false,
    },
    localDate(newVal) {
      this.$emit("update:currentDate", newVal);
    },
    currentDate(newVal) {
      this.localDate = newVal;
    },
    type(newType) {
      this.localType = newType;
    },
    mode(newMode) {
      if (newMode === "doctor") {
        this.checkGoogleAuth().then(() => {
          if (this.doctorHasGoogleAccount) {
            this.fetchEvents();
          } else {
            this.events = [];
          }
        });
      } else if (newMode === "clinic") {
        this.doctorHasGoogleAccount = null;
        this.authChecked = false;
        this.loadClinicsIfClinicMode(true);
      }
    },

    selectedClinics(newVal) {
      if (newVal && this.mode === "clinic") {
        // Carrega médicos das clínicas selecionadas e só então busca eventos
        this.loadDoctorsForSelectedClinics().then(() => this.fetchEvents());
      }
    },
  },

  methods: {
    // fuso de quem está usando (o util já usa o do navegador por padrão)
    formatTime,
    // Utilitários de Data
    parseDate(dateStr) {
      const [year, month, day] = dateStr.split("-").map(Number);
      return new Date(year, month - 1, day);
    },

    getStartOfWeek(date) {
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay());
      return startOfWeek;
    },

    getEndOfWeek(startOfWeek) {
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return endOfWeek;
    },

    formatDateForCalendar(date) {
      // Validar se a data é válida
      if (!date || isNaN(new Date(date).getTime())) {
        console.warn("Data inválida fornecida:", date);
        return `${todayISO()} ${formatTime(new Date())}`;
      }

      const validDate = new Date(date);
      const year = validDate.getFullYear();
      const month = String(validDate.getMonth() + 1).padStart(2, "0");
      const day = String(validDate.getDate()).padStart(2, "0");
      const hours = String(validDate.getHours()).padStart(2, "0");
      const minutes = String(validDate.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    // start vem como "YYYY-MM-DD HH:mm" (formato v-calendar)
    extractTime(startStr) {
      if (!startStr || typeof startStr !== "string") return "";
      const parts = startStr.split(" ");
      return parts.length >= 2 ? parts[1].slice(0, 5) : "";
    },

    formatEventDate(start, end) {
      const startDate = new Date(start);
      const endDate = new Date(end);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return "Data inválida";
      }

      // Detecta o locale do navegador
      const locale = navigator.language || "pt-BR";

      // Formata dia da semana e data
      const startDay = new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
      }).format(startDate);
      const endDay = new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
      }).format(endDate);

      // Formata horário 24h (ou 12h dependendo do locale)
      const startTime = new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(startDate);
      const endTime = new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(endDate);

      return startDay === endDay
        ? `${startDay}, ${startTime} até ${endTime}`
        : `${startDay}, ${startTime} até ${endDay}, ${endTime}`;
    },

    async confirmDeleteEvent(eventId) {
      this.eventToDeleteId = eventId;
      this.selectedOpen = false;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Tem certeza que deseja excluir este evento?",
        hint: "Esta ação não pode ser desfeita.",
      });
      if (!ok) {
        this.eventToDeleteId = null;
        return;
      }
      await this.deleteEvent(this.eventToDeleteId);
      this.eventToDeleteId = null;
    },

    // Eventos do Calendário
    onDateSelected(date) {
      this.localDate = date;
      this.$emit("update:currentDate", this.localDate);
    },

    resetToToday() {
      const today = getLocalDateISO();
      this.localDate = today;
      this.$emit("reset-calendar-to-today");
    },

    viewDay({ date }) {
      this.localType = "day";
      this.$emit("reset-calendar-to-day-type");
    },

    onTimeClicked(timeEvent) {
      // v-calendar dispara click:time mesmo quando o usuário clica em um
      // evento existente (o slot fica por baixo). Ignora o clique nesse caso
      // para que o popover de detalhes (click:event) seja a única ação.
      const target = timeEvent?.nativeEvent?.target;
      if (
        target &&
        typeof target.closest === "function" &&
        target.closest(".v-event, .v-event-timed, .v-event-more, .clinic-event-content")
      ) {
        return;
      }
      this.$emit("time-clicked", timeEvent);
    },

    copyMeetLink(link) {
      if (!link) return;
      navigator.clipboard
        .writeText(link)
        .then(() => this.success("Link do Meet copiado!"))
        .catch(() => this.error("Não foi possível copiar o link."));
    },

    /**
     * Abre a consulta que veio da notificação (salvia-789), uma vez só.
     *
     * Se o evento não estiver na lista — semana errada, ou consulta que já saiu da
     * agenda — não faz nada e não avisa: a notificação continua acessível e o médico
     * vê a agenda normalmente, que é melhor do que um erro sem ação possível.
     */
    abrirEventoDaNotificacaoSePossivel() {
      const alvo = this.eventoAlvoDaNotificacao;
      if (!alvo || !Array.isArray(this.events) || this.events.length === 0) return;

      const evento = this.events.find((e) => String(e?.id) === alvo);
      if (!evento) return;

      this.eventoAlvoDaNotificacao = null;   // uma vez só
      this.showEvent({ nativeEvent: null, event: evento });
    },

    /**
     * Clique no "+N" de um dia do mês. O v-calendar entrega o dia e o clique;
     * o mini diálogo abre logo abaixo do "+N", alinhado à esquerda dele.
     */
    abrirMaisEventos({ nativeEvent, date }) {
      nativeEvent?.stopPropagation();

      const alvo = nativeEvent?.target?.closest?.(".v-event-more") || nativeEvent?.target;
      const caixa = alvo?.getBoundingClientRect?.();

      this.maisEventos = {
        aberto: false,
        data: date,
        x: caixa ? caixa.left : nativeEvent?.clientX || 0,
        y: caixa ? caixa.bottom + 4 : nativeEvent?.clientY || 0,
      };

      // Fecha e reabre no próximo tick: clicar no "+N" de OUTRO dia com o
      // menu aberto precisa movê-lo, e o v-menu só reposiciona ao abrir.
      this.$nextTick(() => {
        this.maisEventos.aberto = true;
      });
    },

    /**
     * Mesma cor do card no mês: `event.color` (o que o v-calendar usa) e, no
     * modo clínica ou em consulta recusada, a borda lateral do status — a
     * regra do slot `event` acima.
     */
    estiloDoItemMais(evento) {
      const estilo = { background: evento.color || "var(--ds-primary)" };
      const comStatus =
        (this.mode === "clinic" || evento.confirmation_state === "declined") &&
        evento.status_color;
      if (comStatus) estilo.borderLeft = `4px solid ${evento.status_color}`;
      return estilo;
    },

    /**
     * Horário do evento NESTE dia. Evento de vários dias mostrava o horário de
     * início do primeiro dia também nos seguintes — o Google Agenda mostra
     * "Dia todo" no meio e o horário de término no último, e aqui também.
     */
    horarioNoDiaMais(evento) {
      const dia = this.maisEventos.data;
      const inicioDia = String(evento.start || "").slice(0, 10);
      const fimDia = String(evento.end || evento.start || "").slice(0, 10);

      if (inicioDia === dia) return this.extractTime(evento.start) || "Dia todo";
      if (fimDia === dia) {
        const fim = this.extractTime(evento.end);
        return fim ? `até ${fim}` : "Dia todo";
      }
      return "Dia todo";
    },

    abrirEventoDaLista(evento, nativeEvent) {
      this.maisEventos.aberto = false;
      this.showEvent({ nativeEvent, event: evento });
    },

    showEvent({ nativeEvent, event }) {
      // Faixas sintéticas (trava/bloqueio) não são clicáveis.
      if (event && event.synthetic) return;
      this.selectedEvent = event;
      this.selectedElement = nativeEvent ? nativeEvent.target : null;
      this.selectedEventDetails = null;
      this.selectedOpen = true;

      // Busca payload completo (paciente, médico, clínica, attendees) on-demand.
      this.loadEventDetails();
    },

    async loadEventDetails() {
      const googleId = this.selectedEvent?.google_id;
      const eventId = this.selectedEvent?.id;

      // salvia-882: consulta que nao chegou ao Google (criada pelo assistente,
      // ou sync falhou) vem sem google_id. Antes saia daqui sem detalhe — sem
      // paciente, o "Iniciar atendimento" nao aparecia. Agora cai no id do
      // projeto. Evento externo (so Google) tem id null e segue pelo google_id.
      let url;
      if (googleId) {
        url = `/api/google-calendar/event-details/${encodeURIComponent(googleId)}`;
      } else if (eventId) {
        url = `/api/google-calendar/event-details-by-id/${encodeURIComponent(eventId)}`;
      } else {
        return;
      }

      this.loadingEventDetails = true;
      try {
        const { data } = await http.get(url);
        this.selectedEventDetails = data;
      } catch (err) {
        console.error("Erro ao carregar detalhes do evento:", err);
        this.erroDeRequisicao(err, "Não foi possível carregar todos os detalhes do evento.");
      } finally {
        this.loadingEventDetails = false;
      }
    },

    // Ações do Evento
    editEvent(event) {
      if (!event?.id) {
        console.error("Evento inválido ou sem id:", event);
        return;
      }
      this.selectedElement = null;
      this.selectedOpen = false;
      this.eventToEditPro = event;
      this.OpenCalendarDialogEditEvent = true;
    },

    async deleteEvent(eventId) {
      if (!eventId) return;

      try {
        this.loading = true;
        await http.delete(`/api/google-calendar/delete-event/${eventId}`);

        this.selectedOpen = false;
        this.success("Evento deletado com sucesso!");
        await this.fetchEvents();
      } catch (error) {
        this.erroDeRequisicao(error, "Erro ao deletar evento.");
        console.error(
          "Erro ao deletar evento:",
          error.response?.data || error.message,
        );
      } finally {
        this.loading = false;
      }
    },
    getBrowserTimeZone: userTimeZone,

    toVCalendarDateTime(isoString, timeZone) {
      const dt = new Date(isoString);

      const day = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(dt); // YYYY-MM-DD

      const time = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(dt); // HH:mm

      return `${day} ${time}`; // v-calendar friendly
    },

    mapApiEventsToCalendarEvents(apiEvents) {
      const fallbackTz = this.getBrowserTimeZone();

      return (apiEvents || []).map((e) => {
        const tz = e.time_zone || fallbackTz;
        const isExternal = e.source === "external";

        return {
          ...e,

          // ✅ preserve ISO for editing / syncing
          start_iso: e.start,
          end_iso: e.end,

          // ✅ adapt for v-calendar display
          start: this.toVCalendarDateTime(e.start, tz),
          end: this.toVCalendarDateTime(e.end, tz),

          // (opcional) v-calendar usa "name" e "details" em alguns exemplos
          name: e.name,
          details: e.description ?? "",

          // Eventos externos ganham cor cinza padrão (o slot do v-calendar
          // também desenha indicadores visuais extras).
          color: isExternal ? "#90A4AE" : e.color,
        };
      });
    },

    isProjectEvent(event) {
      // Default true para compat: se source não vier (chamadas antigas), trata como projeto.
      if (!event) return false;
      return event.source !== "external";
    },

    // ========== DOCTOR METHODS ==========
    async fetchDoctorEvents() {
      if (this.loadingCalendarData) return;
      this.loadingCalendarData = true;

      const calendar = this.$refs.calendar;
      if (!calendar?.lastStart || !calendar?.lastEnd) {
        this.loadingCalendarData = false;
        return;
      }

      const startDate = new Date(calendar.lastStart.date);
      const endDate = new Date(calendar.lastEnd.date);

      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(startDate.getDate() - 1);

      endDate.setHours(23, 59, 59, 999);
      endDate.setDate(endDate.getDate() + 1);

      const params = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      };

      try {
        const { data } = await http.get(
          "/api/google-calendar/get-doctor-events",
          { params },
        );
        this.events = Array.isArray(data?.events)
          ? this.mapApiEventsToCalendarEvents(data.events)
          : [];
      } catch (err) {
        console.error("Erro ao buscar eventos do médico:", err);
        this.events = [];
      } finally {
        this.loadingCalendarData = false;
      }
    },

    async updateDoctorRange({ start, end }) {
      if (this.loadingCalendarData) return;
      this.loadingCalendarData = true;

      const startDate = new Date(`${start.date}T00:00:00`);
      const endDate = new Date(`${end.date}T23:59:59`);

      const params = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      };

      try {
        const { data } = await http.get(
          "/api/google-calendar/get-doctor-events",
          {
            params,
          },
        );

        this.events = Array.isArray(data?.events)
          ? this.mapApiEventsToCalendarEvents(data.events)
          : [];
      } catch (err) {
        console.error("Erro ao atualizar eventos do médico:", err);
        this.events = [];
      } finally {
        this.loadingCalendarData = false;
      }
    },

    // ========== CLINIC METHODS ==========
    async fetchClinicEvents() {
      if (this.loadingCalendarData) return;
      this.loadingCalendarData = true;

      if (!this.selectedClinics || this.selectedClinics.length === 0) {
        this.events = [];
        this.loadingCalendarData = false;
        return;
      }

      const calendar = this.$refs.calendar;
      if (!calendar?.lastStart || !calendar?.lastEnd) {
        this.loadingCalendarData = false;
        return;
      }

      const startDate = new Date(calendar.lastStart.date);
      const endDate = new Date(calendar.lastEnd.date);

      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(startDate.getDate() - 1);

      endDate.setHours(23, 59, 59, 999);
      endDate.setDate(endDate.getDate() + 1);

      try {
        const promises = this.selectedClinics.map((clinic) => {
          const params = this.buildClinicEventsParams(
            clinic.id,
            startDate,
            endDate,
          );
          return http.get("/api/google-calendar/get-clinic-events", { params });
        });

        const responses = await Promise.all(promises);
        let allEvents = [];

        responses.forEach((response) => {
          if (Array.isArray(response.data?.events)) {
            allEvents = allEvents.concat(
              this.mapApiEventsToCalendarEvents(response.data.events),
            );
          }
        });

        this.events = allEvents;
        await this.applyScheduleOverlays(startDate, endDate);
      } catch (err) {
        console.error("Erro ao buscar eventos da clínica:", err);
        this.events = [];
      } finally {
        this.loadingCalendarData = false;
      }
    },

    async updateClinicRange({ start, end }) {
      if (this.loadingCalendarData) return;
      this.loadingCalendarData = true;

      try {
        if (!this.selectedClinics || this.selectedClinics.length === 0) {
          this.events = [];
          return;
        }

        const startDate = new Date(`${start.date}T00:00:00`);
        const endDate = new Date(`${end.date}T23:59:59`);

        const promises = this.selectedClinics.map((clinic) => {
          const params = this.buildClinicEventsParams(
            clinic.id,
            startDate,
            endDate,
          );
          return http.get("/api/google-calendar/get-clinic-events", { params });
        });

        const responses = await Promise.all(promises);
        let allEvents = [];

        responses.forEach((response) => {
          if (Array.isArray(response.data?.events)) {
            allEvents = allEvents.concat(
              this.mapApiEventsToCalendarEvents(response.data.events),
            );
          }
        });

        this.events = allEvents;
        await this.applyScheduleOverlays(startDate, endDate);
      } catch (err) {
        console.error("Erro ao atualizar eventos da clínica:", err);
        this.events = [];
      } finally {
        this.loadingCalendarData = false;
      }
    },

    onClinicChange(newClinics) {
      this.selectedClinics = newClinics;
      // não chama fetch aqui, o watch já chama
    },

    // ========== DOCTORS FILTER METHODS ==========
    buildClinicEventsParams(clinicId, startDate, endDate) {
      const params = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        clinic_id: clinicId,
      };

      // Só envia doctor_ids quando há filtro parcial.
      // Se todos os médicos estão selecionados ou nenhum filtro carregado,
      // o backend devolve todos os eventos da clínica.
      const total = this.availableDoctors.length;
      const selected = this.selectedDoctorIds.length;
      if (total > 0 && selected < total) {
        params["doctor_ids"] = this.selectedDoctorIds;
      }
      return params;
    },

    // ── Fase 2: faixas de trava (cinza escuro) sobre a agenda da clínica ──
    ymd(date) {
      const d = new Date(date);
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },

    lockReasonLabel(lk) {
      const map = {
        congresso: "Congresso",
        aula: "Aula",
        curso: "Curso",
        ferias: "Férias",
        outro: lk.reason_other || "Bloqueio",
      };
      return map[lk.reason] || "Bloqueio";
    },

    // Converte as travas resolvidas em eventos sintéticos (um segmento por dia).
    buildLockedEvents(resolved) {
      const out = [];
      if (!resolved || !resolved.doctors) return out;

      Object.entries(resolved.doctors).forEach(([doctorId, info]) => {
        (info.locked || []).forEach((lk) => {
          if (!lk.starts_at || !lk.ends_at) return;
          const start = new Date(lk.starts_at);
          const end = new Date(lk.ends_at);
          const cursor = new Date(start);
          cursor.setHours(0, 0, 0, 0);

          while (cursor <= end) {
            const dayStart = new Date(cursor);
            const dayEnd = new Date(cursor);
            dayEnd.setHours(23, 59, 0, 0);
            const segStart = start > dayStart ? start : dayStart;
            const segEnd = end < dayEnd ? end : dayEnd;

            out.push({
              name: this.lockReasonLabel(lk),
              start: this.formatDateForCalendar(segStart),
              end: this.formatDateForCalendar(segEnd),
              color: "#37474F",
              synthetic: "locked",
              doctor_id: Number(doctorId),
              timed: true,
            });
            cursor.setDate(cursor.getDate() + 1);
          }
        });
      });
      return out;
    },

    async applyScheduleOverlays(startDate, endDate) {
      this.overlayReady = false;
      this.availabilityByDate = {};
      if (this.mode !== "clinic" || !this.selectedClinics.length) return;
      try {
        const clinicDoctorIds = this.availableDoctors.map((d) => d.id);
        const doctorIds = this.selectedDoctorIds.length
          ? this.selectedDoctorIds
          : clinicDoctorIds;

        // "Bloqueado/disponível" só faz sentido com UM médico em foco.
        const focus = doctorIds.length === 1 ? doctorIds[0] : null;
        const availByDate = {};

        const overlays = [];
        for (const clinic of this.selectedClinics) {
          const resolved = await getResolvedSchedule({
            clinicId: clinic.id,
            doctorIds,
            startDate: this.ymd(startDate),
            endDate: this.ymd(endDate),
          });
          overlays.push(...this.buildLockedEvents(resolved));

          const available = resolved?.doctors?.[focus]?.available;
          if (focus && Array.isArray(available)) {
            available.forEach((w) => {
              (availByDate[w.date] = availByDate[w.date] || []).push({
                from: w.time_from,
                to: w.time_to,
              });
            });
          }
        }

        this.availabilityByDate = availByDate;
        this.overlayReady = !!focus;
        if (overlays.length) this.events = [...this.events, ...overlays];
      } catch (err) {
        // Overlay é complementar: se falhar, a agenda segue mostrando os eventos.
        console.warn("Falha ao aplicar faixas de agenda:", err);
      }
    },

    // Mostra o overlay de bloqueado/disponível para um dia (1 médico em foco).
    showBlockedOverlay() {
      return this.mode === "clinic" && this.overlayReady;
    },

    onDoctorFilterChange(newSelectedIds) {
      this.selectedDoctorIds = newSelectedIds;
      // refetch só quando há clínicas vinculadas válidas
      if (
        this.mode === "clinic" &&
        this.selectedClinics.length > 0 &&
        !this.selectedClinics.some((c) => !c.has_google_account)
      ) {
        this.fetchClinicEvents();
      }
    },

    async loadDoctorsForSelectedClinics() {
      if (this.mode !== "clinic" || this.selectedClinics.length === 0) {
        this.availableDoctors = [];
        this.selectedDoctorIds = [];
        return;
      }

      this.loadingDoctors = true;
      try {
        const responses = await Promise.all(
          this.selectedClinics.map((c) => getDoctorsByClinics(c.id)),
        );

        // Deduplica por id
        const map = new Map();
        responses.forEach((list) => {
          (list || []).forEach((d) => {
            if (!map.has(d.id)) map.set(d.id, d);
          });
        });
        const doctors = Array.from(map.values());

        this.availableDoctors = doctors;

        // Default: todos selecionados. Preserva seleção anterior quando o id ainda existir.
        const allIds = doctors.map((d) => d.id);
        const preserved = this.selectedDoctorIds.filter((id) =>
          allIds.includes(id),
        );
        // Se a seleção anterior cobria tudo (ou estava vazia inicialmente), volta a marcar todos.
        const shouldSelectAll =
          preserved.length === 0 || preserved.length === allIds.length;
        this.selectedDoctorIds = shouldSelectAll ? allIds : preserved;
      } catch (err) {
        console.error("Erro ao carregar médicos das clínicas:", err);
        this.availableDoctors = [];
        this.selectedDoctorIds = [];
      } finally {
        this.loadingDoctors = false;
      }
    },

    // ========== HELPER METHODS ==========
    // Métodos para chamar os específicos baseado no modo atual
    fetchEvents() {
      if (this.mode === "doctor") return this.fetchDoctorEvents();
      if (this.mode === "clinic") return this.fetchClinicEvents();
      return Promise.resolve(); // seguro
    },

    updateRange(data) {
      if (this.mode === "doctor") {
        return this.updateDoctorRange(data);
      } else if (this.mode === "clinic") {
        return this.updateClinicRange(data);
      }
    },

    async loadClinicsIfClinicMode(forceLoadEvents = false) {
      if (this.mode !== "clinic") return;

      this.clinicsLoaded = false;
      try {
        const response = await getMyClinics();

        // Mantém todas as clínicas para exibir no select
        this.clinics = response || [];

        // Seleciona automaticamente a primeira clínica com conta Google, se existir.
        if (this.selectedClinics.length === 0 && this.clinics.length > 0) {
          const linked = this.clinics.find((c) => c.has_google_account);
          if (linked) this.selectedClinics = [linked];
        }

        // Se forceLoadEvents for true, sempre carrega os eventos
        if (forceLoadEvents) {
          if (
            this.selectedClinics.length > 0 &&
            !this.selectedClinics.some((c) => !c.has_google_account)
          ) {
            await this.fetchEvents();
          } else {
            this.events = [];
          }
        } else if (this.selectedClinics.some((c) => !c.has_google_account)) {
          this.events = [];
        }
      } catch (err) {
        console.error("Erro ao carregar clínicas:", err);
        this.clinics = [];
        this.selectedClinics = [];
        this.events = [];
      } finally {
        this.clinicsLoaded = true;
      }
    },

    async checkGoogleAuth() {
      try {
        const { data } = await http.get("/api/check-google-authentication");
        this.doctorHasGoogleAccount = !!data.authenticated;
      } catch (error) {
        console.error(
          "Erro ao verificar autenticação:",
          error.message || error,
        );
        this.doctorHasGoogleAccount = false;
      } finally {
        this.authChecked = true;
      }
    },

    async authenticateWithGoogle() {
      try {
        const { data } = await http.get("/api/auth/google/redirect");
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url);
          return;
        }
        throw new Error("URL de redirecionamento não recebida do servidor");
      } catch (error) {
        console.error("Erro durante autenticação com Google:", error);
        this.erroDeRequisicao(error, "Não foi possível iniciar a autenticação com o Google.");
      }
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   Wrapper — ocupa toda a viewport disponível
   ═══════════════════════════════════════ */
.calendar-page-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px); /* 70px = altura da navbar */
  overflow: hidden;
}

.calendar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 16px;
}

/* ═══════════════════════════════════════
   Toolbar de controles — pill button style
   ═══════════════════════════════════════ */
.calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 10px;
  flex-shrink: 0;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.toolbar-center {
  flex: 1;
  max-width: 420px;
  min-width: 280px;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

/* Data formatada */
.toolbar-date {
  font-size: 15px;
  font-weight: 600;
  color: var(--ds-text-strong);
  margin: 0 0 0 8px;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

/* ═══════════════════════════════════════
   Pill buttons — mesma linguagem do ModeSelect
   ═══════════════════════════════════════ */
.pill-btn {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1.5px solid var(--ds-blgrey-100);
  border-radius: 20px;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, box-shadow 0.15s ease;
  color: var(--ds-text-550);
  font-family: inherit;
  letter-spacing: 0.2px;
}

.pill-btn:hover {
  background-color: var(--ds-blue-bg);
  border-color: var(--ds-text-muted);
  color: var(--ds-text-700);
}

.pill-btn--today:hover,
.pill-btn--today:focus {
  background-color: var(--ds-blue-100);
  border-color: var(--ds-primary);
  color: var(--ds-primary);
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.18);
}

/* Setas de navegação */
.nav-arrows {
  display: flex;
  align-items: center;
  gap: 4px;
}

.arrow-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1.5px solid var(--ds-blgrey-100);
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  color: var(--ds-text-550);
}

.arrow-btn:hover {
  background-color: var(--ds-blue-100);
  border-color: var(--ds-primary);
  color: var(--ds-primary);
}

/* ═══════════════════════════════════════
   Mode badge — pill estilo
   ═══════════════════════════════════════ */
.mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 20px;
  border: 1.5px solid;
  white-space: nowrap;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.mode-badge--doctor {
  background-color: var(--ds-blue-100);
  border-color: var(--ds-primary);
  color: var(--ds-primary);
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.18);
}

.mode-badge--clinic {
  background-color: var(--ds-green-surface);
  border-color: var(--ds-success-light);
  color: var(--ds-success);
  box-shadow: 0 2px 6px rgba(67, 160, 71, 0.18);
}

/* ═══════════════════════════════════════
   Loading bar
   ═══════════════════════════════════════ */
.calendar-loading-bar {
  flex-shrink: 0;
  margin-bottom: 2px;
}

/* ═══════════════════════════════════════
   Calendário — ocupa o restante exato da tela
   ═══════════════════════════════════════ */
.main-calendar {
  flex: 1 1 0;
  min-height: 0; /* essencial para flex funcionar corretamente */
  width: 100%;
  border: 1px solid var(--ds-blue-100);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(31, 77, 63, 0.04);
  background: var(--ds-surface-white);
}

/* ═══════════════════════════════════════
   Conteúdo do evento — borda lateral indica o status
   (cor de fundo do evento é a cor do médico)
   ═══════════════════════════════════════ */
.clinic-event-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 1px;
  height: 100%;
  width: 100%;
  padding: 0 4px;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 1.2;
  color: var(--ds-white);
}

/* Linha principal (horário + nome do paciente) */
.clinic-event-content__main {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  min-width: 0;
}

/* Segunda linha: clínica do agendamento (só na agenda do médico) */
.clinic-event-content__clinic {
  display: flex;
  align-items: center;
  gap: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
  font-weight: 600;
  opacity: 0.85;
}

.clinic-event-content__clinic-icon {
  flex-shrink: 0;
  opacity: 0.9;
}

/* Faixa de trava (cinza escuro) sobre a agenda da clínica */
.locked-band {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 100%;
  width: 100%;
  padding: 2px 6px;
  box-sizing: border-box;
  background: #37474f;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.06) 5px,
    transparent 5px,
    transparent 11px
  );
}
.locked-band__icon {
  flex-shrink: 0;
}
.locked-band__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Overlay de disponibilidade (1 médico em foco): fundo = bloqueado,
   faixas verdes = disponível. Atrás dos eventos, não captura clique. */
.avail-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.avail-overlay__blocked {
  position: absolute;
  inset: 0;
  background: rgba(96, 108, 120, 0.1);
  background-image: repeating-linear-gradient(
    45deg,
    rgba(96, 108, 120, 0.06),
    rgba(96, 108, 120, 0.06) 6px,
    transparent 6px,
    transparent 12px
  );
}
.avail-overlay__free {
  position: absolute;
  left: 0;
  right: 0;
  background: rgba(62, 142, 115, 0.14);
  border-left: 3px solid rgba(62, 142, 115, 0.5);
}

.clinic-event-content--status {
  border-left: 4px solid transparent;
  padding-left: 6px;
}

/* ═══════════════════════════════════════
   Eventos externos (Google Calendar fora do Nutria)
   Estilo discreto: opacidade, listras diagonais, ícone Google.
   ═══════════════════════════════════════ */
.clinic-event-content--external {
  background: repeating-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0.12) 4px,
    transparent 4px,
    transparent 10px
  );
  opacity: 0.92;
  font-style: italic;
}

.external-icon {
  flex-shrink: 0;
  opacity: 0.95;
}

/* Badge e alerta no modal de detalhes */
.external-badge {
  border-color: rgba(255, 255, 255, 0.6) !important;
  color: var(--ds-white) !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
}

.external-alert {
  border-radius: 8px !important;
  font-size: 12.5px;
}

.clinic-event-content__time {
  font-weight: 700;
  opacity: 0.95;
  flex-shrink: 0;
}

.clinic-event-content__name {
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

/* ═══════════════════════════════════════
   Blurred quando bloqueado
   ═══════════════════════════════════════ */
.blurred-container {
  filter: blur(4px);
  pointer-events: none;
  user-select: none;
}

/* ═══════════════════════════════════════
   Overlay de bloqueio
   ═══════════════════════════════════════ */
.block-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1;
}

/* ═══════════════════════════════════════
   Clinic select — chips e seleção
   ═══════════════════════════════════════ */
.clinic-select :deep(.v-select__selections) {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  overflow: hidden;
  gap: 6px;
}

.clinic-select :deep(.v-field__input) {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  overflow: hidden;
  padding-top: 2px;
  padding-bottom: 2px;
}

.clinic-chip-modern {
  max-width: 130px;
  min-width: 110px;
  height: 26px;
  border-radius: 14px;
  background: var(--ds-blue-100) !important;
  color: var(--ds-primary-dark) !important;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 0 6px 0 10px;
  box-shadow: 0 1px 3px rgba(31, 77, 63, 0.12);
}

.chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.clinic-chip-modern .v-chip__close {
  margin-left: 4px;
  font-size: 14px;
  opacity: 0.7;
  flex: 0 0 auto;
}

.chip-counter-text {
  font-size: 12px;
  color: var(--ds-text-soft);
  white-space: nowrap;
  font-weight: 600;
}

/* ═══════════════════════════════════════
   Responsividade
   ═══════════════════════════════════════ */
@media (max-width: 960px) {
  .calendar-page-wrapper {
    height: calc(100vh - 56px);
  }

  .calendar-toolbar {
    flex-wrap: wrap;
    gap: 8px;
  }

  .toolbar-center {
    order: 3;
    width: 100%;
    max-width: 100%;
    min-width: unset;
  }

  .toolbar-date {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .calendar-inner {
    padding: 0 8px;
  }

  .pill-btn {
    padding: 5px 12px;
    font-size: 12px;
  }

  .main-calendar {
    border-radius: 8px;
  }
}

/* ═══ Bloco do Google Meet no popup do evento ═══ */
.meet-block {
  background: var(--ds-green-surface);
  border: 1px solid var(--ds-green-200);
  border-radius: 10px;
  padding: 12px 14px;
}

.meet-join-btn {
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  border-radius: 8px !important;
}
/* salvia-789 — consulta recusada: LARANJA para chamar o medico a decidir.
   O risco no titulo saiu em 10/09: ele dizia "acabou", e nao acabou — o horario
   segue reservado ate o medico cancelar ou remanejar. */
.clinic-event-content--declined {
  font-weight: 600;
}
.clinic-event-content__estado {
  display: block;
  font-size: 9px;
  line-height: 1.2;
  letter-spacing: 0.2px;
  padding: 1px 4px;
  margin-top: 1px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.clinic-event-content__estado--declined {
  background: rgba(255, 152, 0, 0.95);
  color: rgba(0, 0, 0, 0.85);
  font-weight: 700;
}
.clinic-event-content__estado--awaiting {
  background: rgba(255, 193, 7, 0.9);
  color: rgba(0, 0, 0, 0.82);
}
.clinic-event-content__estado--confirmed {
  background: rgba(76, 175, 80, 0.9);
}

/* ═══ "+N" do mês: mini diálogo com os eventos do dia ═══ */
.mais-eventos {
  border-radius: 12px !important;
  overflow: hidden;
  background: var(--ds-surface-white, #fff) !important;
}
.mais-eventos__topo {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 8px 4px 14px;
}
.mais-eventos__data {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  padding-left: 28px; /* equilibra o botão de fechar, para a data ficar no centro */
}
.mais-eventos__semana {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--ds-text-soft, #78909c);
}
.mais-eventos__dia {
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 1.2;
  color: var(--ds-text, #2c3e50);
}
.mais-eventos__lista {
  display: grid;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px 10px 12px;
}
.mais-eventos__item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 26px;
  padding: 3px 8px;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-size: 0.78rem;
  text-align: left;
  cursor: pointer;
  transition: filter 0.12s ease, transform 0.12s ease;
}
.mais-eventos__item:hover,
.mais-eventos__item:focus-visible {
  outline: none;
  filter: brightness(1.08);
  transform: translateX(2px);
}
.mais-eventos__hora {
  flex-shrink: 0;
  font-weight: 700;
}
.mais-eventos__nome {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* salvia-789: mesma leitura de "recusada" dos cards do mês. */
.mais-eventos__item--recusado {
  opacity: 0.6;
}
.mais-eventos__item--recusado .mais-eventos__nome {
  text-decoration: line-through;
}
.mais-eventos__vazio {
  margin: 8px 0;
  text-align: center;
  font-size: 0.8rem;
  color: var(--ds-text-muted, #90a4ae);
}
</style>
